import { useCallback } from "react";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext.tsx";

/**
 * A per-user UI setting that follows the user across devices.
 *
 * Backed by `ui_preferences` on the user (saved through PATCH /auth/me). The
 * value is applied locally before the save round-trips; a failed save is
 * reported but leaves the local choice in place for the session. `validate`
 * guards against stale or malformed stored values: when it rejects, the
 * fallback is used.
 */
export function useUiPreference<T>(
  key: string,
  fallback: T,
  validate: (value: unknown) => value is T,
): [T, (next: T | null) => void] {
  const { user, updateUiPreferences } = useAuth();
  const stored = user?.ui_preferences?.[key];
  const value = validate(stored) ? stored : fallback;
  const setValue = useCallback(
    (next: T | null) => {
      updateUiPreferences({ [key]: next }).catch(() => {
        toast.error("Could not save your preference.");
      });
    },
    [key, updateUiPreferences],
  );
  return [value, setValue];
}
